import React, { useState, useEffect, useCallback, useMemo, useContext } from "react";
import { Button, Input } from "reactstrap";
import * as XLSX from "xlsx";
import Header from "../../layout/Header/Header";
import { AuthContext } from "../../contexts/AuthContext";
import { posGet, avisaError, dinero, numero, variante } from "./posApi";
import { useTienda, TiendaSelector } from "./TiendaSelector";
import SinPermiso from "./SinPermiso";
import FotoModelo from "../Articulos/FotoModelo";

// Ventas de cada artículo desde su última entrada hasta hoy (no usa fechas)
const corta = (f) => (f ? new Date(f).toLocaleDateString("es-MX", { day: "2-digit", month: "2-digit", year: "2-digit" }) : "");
const hace = (d) => (d === null || d === undefined ? "" : d === 0 ? "hoy" : d === 1 ? "hace 1 día" : `hace ${d} días`);
const colorPct = (p) => (p >= 50 ? "#059669" : p >= 20 ? "#d97706" : "#dc2626");
const fondoPct = (p) => (p >= 50 ? "#10b981" : p >= 20 ? "#f59e0b" : "#ef4444");
// [clave, nombre, filtro, ayuda, color]
const GRUPOS = [
  ["", "Todos", null, "", "#334155"],
  ["bien", "Se vende bien", (p) => p >= 50, "50% o más de lo que entró", "#10b981"],
  ["regular", "Regular", (p) => p >= 20 && p < 50, "Entre 20% y 50%", "#f59e0b"],
  ["lento", "No se mueve", (p) => p < 20, "Menos de 20%", "#ef4444"],
];
const valor = (f, k) => {
  if (k === "ultimaEntrada") return new Date(f.ultimaEntrada.fecha).getTime();
  if (k === "ultimaVenta") return f.ultimaVenta ? new Date(f.ultimaVenta).getTime() : 0;
  if (k === "articulo") return f.codigo;
  return f[k] || 0;
};

function PosVentasArticulo() {
  const { user } = useContext(AuthContext);
  const ctx = useTienda(true);
  const { tienda, nombreTienda } = ctx;
  const todas = tienda === "todas";
  const [detalle, setDetalle] = useState(false);
  const [linea, setLinea] = useState("");
  const [lineas, setLineas] = useState([]);
  const [texto, setTexto] = useState("");
  const [grupo, setGrupo] = useState("");
  const [orden, setOrden] = useState({ k: "pct", dir: -1 });
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);

  useEffect(() => { posGet("/catalogos").then((c) => setLineas(c.lineas || [])).catch(() => {}); }, []);

  const cargar = useCallback(() => {
    if (!tienda) return;
    setCargando(true);
    posGet("/reportes/articulos", { area: tienda, detalle: detalle ? "Si" : undefined, linea: linea || undefined })
      .then(setDatos).catch(avisaError).finally(() => setCargando(false));
  }, [tienda, detalle, linea]);
  useEffect(() => { cargar(); }, [cargar]);

  const buscadas = useMemo(() => {
    if (!datos) return [];
    const t = texto.trim().toLowerCase();
    return t ? datos.filas.filter((f) => `${f.codigo} ${f.nombre} ${f.linea} ${f.color || ""} ${f.talla || ""}`.toLowerCase().includes(t)) : datos.filas;
  }, [datos, texto]);
  const cuenta = (g) => (g[2] ? buscadas.filter((f) => g[2](f.pct)).length : buscadas.length);
  const filas = useMemo(() => {
    const g = GRUPOS.find((x) => x[0] === grupo);
    const base = g && g[2] ? buscadas.filter((f) => g[2](f.pct)) : buscadas;
    return [...base].sort((a, b) => {
      const va = valor(a, orden.k), vb = valor(b, orden.k);
      return (typeof va === "string" ? va.localeCompare(vb) : va - vb) * orden.dir || String(a.codigo).localeCompare(String(b.codigo));
    });
  }, [buscadas, grupo, orden]);
  const tot = useMemo(() => {
    const s = (k) => filas.reduce((t, f) => t + (f[k] || 0), 0);
    const vendidas = s("vendidas"), base = s("base");
    return { tickets: s("tickets"), vendidas, base, importe: s("importe"), stock: s("stock"), pct: base ? Math.round((vendidas / base) * 1000) / 10 : 0 };
  }, [filas]);

  const ordenar = (k) => setOrden((o) => ({ k, dir: o.k === k ? -o.dir : k === "articulo" ? 1 : -1 }));

  const excel = () => {
    const hoja = filas.map((f) => ({
      Codigo: f.codigo, Modelo: f.nombre, Linea: f.linea, ...(detalle ? { Color: f.color || "", Talla: f.talla || "" } : {}),
      UltimaEntrada: corta(f.ultimaEntrada.fecha), PiezasUltimaEntrada: f.ultimaEntrada.cantidad, DiasDesdeEntrada: f.ultimaEntrada.dias,
      ...(todas ? { TiendaUltimaEntrada: f.ultimaEntrada.tienda, TiendasConEntrada: f.ultimaEntrada.tiendas } : {}),
      PiezasQueEntraron: f.base, Tickets: f.tickets, Vendidas: f.vendidas, PctVendido: f.pct, Importe: f.importe,
      PrecioPromedio: f.precioPromedio, PrecioLista: f.precioLista, Existencia: f.existencia, EnCamino: f.enCamino,
      UltimaVenta: corta(f.ultimaVenta), DiasSinVenta: f.diasSinVenta === null ? "" : f.diasSinVenta,
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(hoja), "Desde ultima entrada");
    XLSX.writeFile(wb, `ventas_desde_ultima_entrada_${(nombreTienda || "tiendas").replace(/\s+/g, "_")}.xlsx`);
  };

  if (!(user && user.rolTiendas && user.posReportes)) return <SinPermiso que="ver reportes" />;

  const th = (k, l, al = "right", ayuda) => (
    <th key={k} title={ayuda} onClick={() => ordenar(k)}
      style={{ position: "sticky", top: 0, zIndex: 2, cursor: "pointer", whiteSpace: "nowrap", userSelect: "none", textAlign: al, padding: "12px 12px", fontSize: "0.78rem" }}>
      {l}{orden.k === k && <i className={`fas fa-caret-${orden.dir < 0 ? "down" : "up"} ml-1`} />}
    </th>
  );
  const td = { padding: "12px 12px", verticalAlign: "middle" };
  // Fila de total pegada abajo de la tabla (se queda visible al hacer scroll)
  const tdPie = { ...td, position: "sticky", bottom: 0, zIndex: 2, background: "#e2e8f0", borderTop: "2px solid #94a3b8", color: "#0f172a", textTransform: "none", fontSize: "0.95rem", letterSpacing: "normal", fontWeight: 800 };

  return (
    <>
      <Header />
      <div className="card container col-12" style={{ padding: "18px 22px" }}>
        <div className="page-header">
          <div>
            <h3 className="page-title"><i className="fas fa-list-ol mr-2" style={{ color: "#2563eb" }} />Ventas por artículo</h3>
            <p className="page-subtitle">Lo que se ha vendido de cada modelo{detalle ? " (por talla y color)" : ""} desde su última entrada hasta hoy · {nombreTienda || "Todas las tiendas"}</p>
          </div>
          <div className="page-actions">
            <Button color="secondary" size="sm" href="/PosReportes"><i className="fas fa-th-large mr-1" />Reportes</Button>
          </div>
        </div>

        <div className="filter-bar">
          <TiendaSelector ctx={ctx} permiteTodas />
          <div style={{ minWidth: 170 }}><label>Línea</label>
            <Input type="select" value={linea} onChange={(e) => setLinea(e.target.value)}>
              <option value="">Todas</option>{lineas.map((l) => <option key={l._id} value={l._id}>{l.name}</option>)}
            </Input>
          </div>
          <div><label>Ver</label>
            <Input type="select" value={detalle ? "det" : "mod"} onChange={(e) => setDetalle(e.target.value === "det")}>
              <option value="mod">Por modelo</option><option value="det">Por talla y color</option>
            </Input>
          </div>
          <div style={{ flex: 1, minWidth: 220 }}><label>Buscar</label><Input value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="Código, modelo, línea, color o talla" /></div>
          <div style={{ flexDirection: "row", gap: 8 }}>
            <Button color="primary" onClick={cargar} disabled={cargando} title="Actualizar">{cargando ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-sync" />}</Button>{" "}
            <Button color="warning" onClick={excel} disabled={!filas.length}><i className="fas fa-file-excel mr-1" />Excel</Button>
          </div>
        </div>

        {datos && (
          <div className="row mb-3" style={{ rowGap: 10 }}>
            {[
              ["% vendido de lo que entró", `${tot.pct}%`, colorPct(tot.pct), `${numero(tot.vendidas)} de ${numero(tot.base)} piezas`],
              ["Piezas vendidas", numero(tot.vendidas), "#0f172a", `en ${numero(tot.tickets)} tickets`],
              ["Importe", dinero(tot.importe), "#0f172a", "desde la última entrada"],
              ["Stock", numero(tot.stock), "#0f172a", `${numero(filas.length)} ${detalle ? "variantes" : "modelos"}`],
            ].map(([l, v, c, sub]) => (
              <div key={l} className="col-6 col-lg-3">
                <div style={{ border: "1px solid #e2e8f0", borderRadius: 12, padding: "12px 16px", background: "#fff", height: "100%" }}>
                  <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: ".5px" }}>{l}</div>
                  <div style={{ fontSize: "1.7rem", fontWeight: 800, color: c, lineHeight: 1.2 }}>{v}</div>
                  <div style={{ fontSize: "0.8rem", color: "#64748b" }}>{sub}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="d-flex flex-wrap mb-2" style={{ gap: 8 }}>
          {GRUPOS.map((g) => {
            const activo = grupo === g[0];
            return (
              <button key={g[0]} type="button" onClick={() => setGrupo(g[0])} title={g[3]}
                style={{ border: `2px solid ${activo ? g[4] : "#e2e8f0"}`, background: activo ? `${g[4]}14` : "#fff", color: "#0f172a", borderRadius: 999, padding: "6px 16px", fontWeight: 600, fontSize: "0.9rem" }}>
                {g[0] && <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 5, background: g[4], marginRight: 8 }} />}
                {g[1]} <span style={{ color: "#64748b", fontWeight: 700, marginLeft: 4 }}>{numero(cuenta(g))}</span>
              </button>
            );
          })}
        </div>

        <div className="table-responsive" style={{ maxHeight: "68vh", overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 12 }}>
          <table className="table table-dark-header mb-0" style={{ fontSize: "0.95rem" }}>
            <thead>
              <tr>
                {th("articulo", "Artículo", "left")}
                {th("ultimaEntrada", "Última entrada", "left", "Fecha y piezas de la última entrada")}
                {th("tickets", "Tickets", "right", "Tickets desde la última entrada")}
                {th("vendidas", "Vendidas", "right", "Piezas vendidas (menos devueltas) de las que entraron")}
                {th("pct", "% vendido", "left", "Vendidas ÷ piezas que entraron")}
                {th("importe", "Importe")}
                {th("precioPromedio", "Precio")}
                {th("stock", "Stock", "right", "Existencia + lo que viene en camino")}
                {th("ultimaVenta", "Última venta", "left")}
              </tr>
            </thead>
            <tbody>
              {!filas.length && (
                <tr><td colSpan={9} className="text-center text-muted py-5">{cargando || !datos ? "Cargando…" : "No hay artículos con entradas"}</td></tr>
              )}
              {filas.slice(0, 3000).map((f, i) => (
                <tr key={`${f.articulos}-${f.color || ""}-${f.talla || ""}`} style={{ background: i % 2 ? "#fbfcfe" : "#fff", color: f.activo ? undefined : "#94a3b8" }}>
                  <td style={{ ...td, minWidth: 250 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <FotoModelo url={f.urlFoto} nombre={`${f.codigo} · ${f.nombre}`} size={46} />
                      <div style={{ lineHeight: 1.3 }}>
                        <div style={{ fontWeight: 800 }}>{f.codigo}</div>
                        <div>{f.nombre}</div>
                        <div style={{ fontSize: "0.8rem", color: "#64748b" }}>{[f.linea, variante(f)].filter(Boolean).join(" · ")}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ ...td, whiteSpace: "nowrap", lineHeight: 1.3 }}>
                    <div style={{ fontWeight: 700 }}>{corta(f.ultimaEntrada.fecha)}</div>
                    <div style={{ fontSize: "0.82rem", color: "#64748b" }}>
                      {todas && f.ultimaEntrada.tiendas > 1 ? `${numero(f.base)} pzs en ${f.ultimaEntrada.tiendas} tiendas` : `${numero(f.ultimaEntrada.cantidad)} pzs`} · {hace(f.ultimaEntrada.dias)}
                    </div>
                  </td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 700 }}>{numero(f.tickets)}</td>
                  <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap", lineHeight: 1.3 }}>
                    <span style={{ fontWeight: 800, fontSize: "1.05rem" }}>{numero(f.vendidas)}</span> <span style={{ color: "#64748b" }}>de {numero(f.base)}</span>
                    {f.devueltas > 0 && <div style={{ fontSize: "0.78rem", color: "#dc2626" }}>{f.devueltas} devuelta{f.devueltas > 1 ? "s" : ""}</div>}
                  </td>
                  <td style={{ ...td, minWidth: 170 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 90, height: 10, background: "#e2e8f0", borderRadius: 5, overflow: "hidden", flex: "none" }}>
                        <div style={{ width: `${Math.min(f.pct, 100)}%`, height: "100%", background: fondoPct(f.pct), borderRadius: 5 }} />
                      </div>
                      <span style={{ fontWeight: 800, fontSize: "1.05rem", color: colorPct(f.pct) }}>{f.pct}%</span>
                    </div>
                  </td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 700, whiteSpace: "nowrap" }}>{f.importe ? dinero(f.importe) : <span className="text-muted">—</span>}</td>
                  <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap", lineHeight: 1.3 }}>
                    <div style={{ fontWeight: 700 }}>{dinero(f.precioPromedio || f.precioLista)}</div>
                    {f.precioPromedio > 0 && f.precioPromedio < f.precioLista - 0.01 && <div style={{ fontSize: "0.78rem", color: "#94a3b8", textDecoration: "line-through" }}>{dinero(f.precioLista)}</div>}
                  </td>
                  <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap", lineHeight: 1.3, color: f.stock <= 0 ? "#dc2626" : undefined }}>
                    <span style={{ fontWeight: 700 }}>{numero(f.stock)}</span>
                    {f.enCamino > 0 && <div style={{ fontSize: "0.78rem", color: "#64748b" }}>+{f.enCamino} en camino</div>}
                  </td>
                  <td style={{ ...td, whiteSpace: "nowrap", lineHeight: 1.3 }}>
                    {f.ultimaVenta ? (
                      <><div style={{ fontWeight: 600 }}>{corta(f.ultimaVenta)}</div>
                        <div style={{ fontSize: "0.8rem", color: f.diasSinVenta > 14 ? "#dc2626" : "#64748b" }}>{hace(f.diasSinVenta)}</div></>
                    ) : <span style={{ color: "#dc2626", fontWeight: 600 }}>Sin venta</span>}
                  </td>
                </tr>
              ))}
            </tbody>
            {filas.length > 0 && (
              <tfoot>
                <tr style={{ fontWeight: 800 }}>
                  <td style={tdPie}>TOTAL · {numero(filas.length)}</td>
                  <td style={tdPie}><span style={{ fontWeight: 600, color: "#64748b" }}>{numero(tot.base)} pzs entraron</span></td>
                  <td style={{ ...tdPie, textAlign: "right" }}>{numero(tot.tickets)}</td>
                  <td style={{ ...tdPie, textAlign: "right" }}>{numero(tot.vendidas)}</td>
                  <td style={{ ...tdPie, color: colorPct(tot.pct) }}>{tot.pct}%</td>
                  <td style={{ ...tdPie, textAlign: "right" }}>{dinero(tot.importe)}</td>
                  <td style={tdPie} />
                  <td style={{ ...tdPie, textAlign: "right" }}>{numero(tot.stock)}</td>
                  <td style={tdPie} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        {filas.length > 3000 && <div className="text-muted mt-1">Mostrando 3,000 de {numero(filas.length)}; usa el buscador o descarga el Excel.</div>}
      </div>
    </>
  );
}

export default PosVentasArticulo;
